import { createFileRoute, Link } from "@tanstack/react-router";
import { useMemo, useState } from "react";
import { useQueryClient } from "@tanstack/react-query";
import { useServerFn } from "@tanstack/react-start";
import { useAuth } from "@/lib/auth";
import { Button } from "@/components/ui/button";
import { toast } from "sonner";
import { ArrowLeft, ChevronLeft, ChevronRight } from "lucide-react";
import { claimContractorShift, requestContractorReplacement } from "@/lib/contractors.functions";
import { contractedShiftStatus, shiftHours, SHIFT_STATUS_LABEL, shiftStatusClasses } from "@/lib/contractors";
import { ShiftStatusBadge } from "@/components/contractors/ShiftStatusBadge";
import { useContractorWorkspace } from "./contractor.index";

export const Route = createFileRoute("/_authenticated/contractor/schedule")({
  component: ContractorSchedulePage,
  head: () => ({
    meta: [
      { title: "Contracted schedule — MissionReady" },
      { name: "description", content: "Month, week and day views of your contracted shifts, with the unclaimed shifts below." },
      { property: "og:title", content: "Contracted schedule — MissionReady" },
      { property: "og:description", content: "Contracted shift calendar for your contractor organization." },
      { property: "og:type", content: "website" },
      { name: "twitter:card", content: "summary" },
    ],
  }),
});

type View = "month" | "week" | "day";

function startOfDay(d: Date) { const x = new Date(d); x.setHours(0, 0, 0, 0); return x; }
function addDays(d: Date, n: number) { const x = new Date(d); x.setDate(x.getDate() + n); return x; }
function sameDay(a: Date, b: Date) { return startOfDay(a).getTime() === startOfDay(b).getTime(); }

function rangeFor(view: View, anchor: Date): Date[] {
  if (view === "day") return [startOfDay(anchor)];
  if (view === "week") {
    const start = addDays(startOfDay(anchor), -startOfDay(anchor).getDay());
    return Array.from({ length: 7 }, (_, i) => addDays(start, i));
  }
  const first = new Date(anchor.getFullYear(), anchor.getMonth(), 1);
  const gridStart = addDays(first, -first.getDay());
  return Array.from({ length: 42 }, (_, i) => addDays(gridStart, i));
}

function ContractorSchedulePage() {
  const { user } = useAuth();
  const qc = useQueryClient();
  const { data, isLoading, error } = useContractorWorkspace();
  const claimFn = useServerFn(claimContractorShift);
  const replaceFn = useServerFn(requestContractorReplacement);

  const [view, setView] = useState<View>("month");
  const [anchor, setAnchor] = useState(() => new Date());
  const [selected, setSelected] = useState<string | null>(null);
  const [busy, setBusy] = useState<string | null>(null);

  const shifts: any[] = data?.shifts ?? [];
  const sites: any[] = data?.sites ?? [];
  const teammates: any[] = data?.teammates ?? [];
  const refresh = () => qc.invalidateQueries({ queryKey: ["contractor-workspace", user?.id ?? "anon"] });

  const days = useMemo(() => rangeFor(view, anchor), [view, anchor]);
  const siteName = (id: string) => sites.find((s) => s.id === id)?.name ?? "Assigned site";
  const memberName = (id: string | null) => {
    if (!id) return "Unclaimed";
    if (id === user?.id) return "You";
    const t = teammates.find((x) => x.user_id === id);
    return t?.profile?.full_name ?? t?.profile?.email ?? "Contractor member";
  };

  const shiftsOn = (d: Date) => shifts.filter((s) => sameDay(new Date(s.starts_at), d));
  const unclaimed = shifts.filter((s) => !s.officer_id && s.status !== "cancelled");
  const selectedShift = shifts.find((s) => s.id === selected) ?? null;

  const move = (dir: number) => {
    if (view === "day") setAnchor(addDays(anchor, dir));
    else if (view === "week") setAnchor(addDays(anchor, dir * 7));
    else setAnchor(new Date(anchor.getFullYear(), anchor.getMonth() + dir, 1));
  };

  const claim = async (id: string) => {
    setBusy(id);
    try { await claimFn({ data: { shiftId: id } }); toast.success("Shift claimed"); await refresh(); }
    catch (err: any) {
      const msg = typeof err?.message === "string" ? err.message : "Could not claim this shift";
      toast.error(msg.includes("already been claimed") ? "Someone claimed that shift first" : msg);
      await refresh();
    } finally { setBusy(null); }
  };
  const replace = async (id: string) => {
    if (typeof window !== "undefined" && !window.confirm("Return this shift to the claim board?")) return;
    setBusy(id);
    try { await replaceFn({ data: { shiftId: id } }); toast.success("Replacement requested"); await refresh(); }
    catch (err: any) { toast.error(typeof err?.message === "string" ? err.message : "Could not request a replacement"); }
    finally { setBusy(null); }
  };

  const heading =
    view === "day"
      ? anchor.toLocaleDateString(undefined, { weekday: "long", month: "long", day: "numeric" })
      : view === "week"
        ? `Week of ${days[0]!.toLocaleDateString(undefined, { month: "short", day: "numeric" })}`
        : anchor.toLocaleDateString(undefined, { month: "long", year: "numeric" });

  return (
    <div className="mx-auto max-w-3xl px-4 pb-28 pt-4 sm:pt-6">
      <Link to="/contractor" className="mb-3 inline-flex min-h-10 items-center gap-1 text-sm text-muted-foreground hover:text-foreground">
        <ArrowLeft className="h-4 w-4" aria-hidden /> Contracted work
      </Link>
      <h1 className="font-display text-2xl font-semibold">Contracted schedule</h1>

      <div className="mt-4 flex flex-wrap items-center justify-between gap-2">
        <div className="flex items-center gap-1">
          <Button size="icon" variant="outline" className="min-h-11 min-w-11" aria-label="Previous" onClick={() => move(-1)}>
            <ChevronLeft className="h-4 w-4" aria-hidden />
          </Button>
          <span className="px-2 text-sm font-medium">{heading}</span>
          <Button size="icon" variant="outline" className="min-h-11 min-w-11" aria-label="Next" onClick={() => move(1)}>
            <ChevronRight className="h-4 w-4" aria-hidden />
          </Button>
        </div>
        <div role="tablist" aria-label="Calendar view" className="flex gap-1 rounded-lg border border-border p-1">
          {(["month", "week", "day"] as View[]).map((v) => (
            <button
              key={v}
              role="tab"
              aria-selected={view === v}
              onClick={() => setView(v)}
              className={`min-h-10 rounded-md px-3 text-sm capitalize ${view === v ? "bg-primary text-primary-foreground" : "text-muted-foreground"}`}
            >
              {v}
            </button>
          ))}
        </div>
      </div>

      {isLoading ? (
        <p className="mt-6 text-sm text-muted-foreground">Loading the schedule…</p>
      ) : error ? (
        <p className="mt-6 rounded-md border border-destructive/40 bg-destructive/10 p-3 text-sm text-destructive">
          Data source unavailable. Try again shortly.
        </p>
      ) : (
        <>
          {view === "month" ? (
            <>
              <div className="mt-4 grid grid-cols-7 gap-1 text-center text-[11px] uppercase text-muted-foreground">
                {["S", "M", "T", "W", "T", "F", "S"].map((d, i) => <div key={i}>{d}</div>)}
              </div>
              <div className="mt-1 grid grid-cols-7 gap-1">
                {days.map((d) => {
                  const list = shiftsOn(d);
                  const other = d.getMonth() !== anchor.getMonth();
                  return (
                    <button
                      key={d.toISOString()}
                      onClick={() => { setAnchor(d); setView("day"); }}
                      className={`min-h-16 rounded-md border p-1 text-left align-top text-xs ${other ? "border-border/40 text-muted-foreground/60" : "border-border"}`}
                      aria-label={`${d.toDateString()}, ${list.length} contracted shift${list.length === 1 ? "" : "s"}`}
                    >
                      <span className="font-semibold">{d.getDate()}</span>
                      <span className="mt-1 flex flex-col gap-0.5">
                        {list.slice(0, 2).map((s) => {
                          const st = contractedShiftStatus(s);
                          return (
                            <span key={s.id} className={`truncate rounded border px-1 text-[10px] ${shiftStatusClasses(st)}`}>
                              {SHIFT_STATUS_LABEL[st]}
                            </span>
                          );
                        })}
                        {list.length > 2 ? <span className="text-[10px] text-muted-foreground">+{list.length - 2} more</span> : null}
                      </span>
                    </button>
                  );
                })}
              </div>
            </>
          ) : (
            <ul className="mt-4 space-y-3">
              {days.map((d) => {
                const list = shiftsOn(d);
                return (
                  <li key={d.toISOString()}>
                    <h2 className="text-sm font-semibold">
                      {d.toLocaleDateString(undefined, { weekday: "long", month: "short", day: "numeric" })}
                    </h2>
                    <ul className="mt-2 space-y-2">
                      {list.length === 0 ? (
                        <li className="text-xs text-muted-foreground">No contracted shifts.</li>
                      ) : list.map((s) => (
                        <li key={s.id}>
                          <button
                            onClick={() => setSelected(s.id)}
                            className="w-full rounded-lg border border-border bg-card p-3 text-left"
                          >
                            <div className="flex items-center justify-between gap-2">
                              <span className="text-sm font-medium">
                                {new Date(s.starts_at).toLocaleTimeString(undefined, { hour: "numeric", minute: "2-digit" })} –{" "}
                                {new Date(s.ends_at).toLocaleTimeString(undefined, { hour: "numeric", minute: "2-digit" })}
                              </span>
                              <ShiftStatusBadge status={contractedShiftStatus(s)} />
                            </div>
                            <p className="mt-1 text-xs text-muted-foreground">
                              {siteName(s.site_id)} · {memberName(s.officer_id)}
                            </p>
                          </button>
                        </li>
                      ))}
                    </ul>
                  </li>
                );
              })}
            </ul>
          )}

          {selectedShift ? (
            <div className="mt-5 rounded-xl border border-primary/40 bg-card p-4">
              <div className="flex items-start justify-between gap-2">
                <div>
                  <h2 className="font-display text-lg font-semibold">{siteName(selectedShift.site_id)}</h2>
                  <p className="mt-1 text-sm">
                    {new Date(selectedShift.starts_at).toLocaleString()} → {new Date(selectedShift.ends_at).toLocaleString()}
                  </p>
                  <p className="mt-0.5 text-xs text-muted-foreground">
                    {shiftHours(selectedShift.starts_at, selectedShift.ends_at)} hours ·{" "}
                    {selectedShift.role_label ?? "Contracted post"} · {memberName(selectedShift.officer_id)}
                  </p>
                  {selectedShift.notes ? <p className="mt-2 text-xs text-muted-foreground">{selectedShift.notes}</p> : null}
                </div>
                <ShiftStatusBadge status={contractedShiftStatus(selectedShift)} />
              </div>
              <div className="mt-3 flex flex-wrap gap-2">
                {!selectedShift.officer_id && selectedShift.status !== "cancelled" ? (
                  <Button className="min-h-12 flex-1" disabled={busy === selectedShift.id} onClick={() => claim(selectedShift.id)}>
                    Claim
                  </Button>
                ) : null}
                {selectedShift.officer_id === user?.id ? (
                  <Button variant="outline" className="min-h-12 flex-1" disabled={busy === selectedShift.id}
                    onClick={() => replace(selectedShift.id)}>
                    Find replacement
                  </Button>
                ) : null}
                <Button variant="ghost" className="min-h-12" onClick={() => setSelected(null)}>Close</Button>
              </div>
            </div>
          ) : null}

          <section className="mt-8" aria-labelledby="sched-unclaimed">
            <h2 id="sched-unclaimed" className="font-display text-lg font-semibold">
              Unclaimed shifts <span className="text-sm font-normal text-muted-foreground">({unclaimed.length})</span>
            </h2>
            <ul className="mt-3 space-y-2">
              {unclaimed.length === 0 ? (
                <li className="text-sm text-muted-foreground">Nothing waiting to be claimed.</li>
              ) : unclaimed.map((s) => (
                <li key={s.id} className="flex flex-wrap items-center justify-between gap-2 rounded-lg border border-border bg-card p-3">
                  <div className="min-w-0">
                    <p className="text-sm font-medium">
                      {new Date(s.starts_at).toLocaleString(undefined, { weekday: "short", month: "short", day: "numeric", hour: "numeric", minute: "2-digit" })}
                    </p>
                    <p className="text-xs text-muted-foreground">
                      {siteName(s.site_id)} · {shiftHours(s.starts_at, s.ends_at)} h
                    </p>
                  </div>
                  <Button size="sm" className="min-h-11" disabled={busy === s.id} onClick={() => claim(s.id)}>Claim</Button>
                </li>
              ))}
            </ul>
          </section>
        </>
      )}
    </div>
  );
}
